import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Badge,
  Button,
  Grid
} from '@mui/material';
import {
  People,
  PersonAdd,
  Search,
  Timeline,
  Refresh
} from '@mui/icons-material';
import FriendsList from './FriendsList';
import FriendRequests from './FriendRequests';
import FriendSearch from './FriendSearch';
import ActivityFeed from '../Social/ActivityFeed';
import FriendLeaderboard from '../Social/FriendLeaderboard';
import { useFriends } from '../../hooks/useFriends';
import { useSocial } from '../../hooks/useSocial';

const FriendsPageComponent = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const {
    friends,
    friendRequests,
    searchResults,
    loading: friendsLoading,
    searchLoading,
    error: friendsError,
    searchUsers,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    removeFriend,
    refreshData: refreshFriends,
    setError: setFriendsError,
    clearSearchResults
  } = useFriends();

  const {
    activityFeed,
    leaderboard,
    loading: socialLoading,
    error: socialError,
    toggleLike,
    addComment,
    fetchLeaderboard,
    refreshFeed,
    setError: setSocialError
  } = useSocial();

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 2) {
      clearSearchResults();
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      await sendFriendRequest(userId);
      showMessage('Friend request sent successfully!', 'success');
    } catch (error) {
      showMessage('Failed to send friend request. Please try again.', 'error');
    }
  };

  const handleAcceptFriendRequest = async (requestId) => {
    try {
      await acceptFriendRequest(requestId);
      showMessage('Friend request accepted!', 'success');
    } catch (error) {
      showMessage('Failed to accept friend request. Please try again.', 'error');
    }
  };

  const handleDeclineFriendRequest = async (requestId) => {
    try {
      await declineFriendRequest(requestId);
      showMessage('Friend request declined.', 'info');
    } catch (error) {
      showMessage('Failed to decline friend request. Please try again.', 'error');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    try {
      await removeFriend(friendId);
      showMessage('Friend removed successfully.', 'info');
    } catch (error) {
      showMessage('Failed to remove friend. Please try again.', 'error');
    }
  };

  const handleViewProfile = (userId) => {
    showMessage('Profile viewing feature coming soon!', 'info');
  };

  const handleSendMessage = (userId) => {
    showMessage('Messaging feature coming soon!', 'info');
  };

  const handleLike = async (activityId, shouldLike) => {
    try {
      await toggleLike(activityId, shouldLike);
    } catch (error) {
      showMessage('Failed to update like. Please try again.', 'error');
    }
  };

  const handleComment = async (activityId, comment) => {
    try {
      await addComment(activityId, comment);
      showMessage('Comment added!', 'success');
    } catch (error) {
      showMessage('Failed to add comment. Please try again.', 'error');
    }
  };

  const handleRefresh = () => {
    if (activeTab === 0 || activeTab === 1) {
      refreshFriends();
    } else if (activeTab === 3) {
      refreshFeed();
    }
    showMessage('Data refreshed!', 'success');
  };

  const getTabLabel = (index, count) => {
    switch (index) {
      case 0:
        return `My Friends ${count > 0 ? `(${count})` : ''}`;
      case 1:
        return count > 0 ? (
          <Badge badgeContent={count} color="error">
            Friend Requests
          </Badge>
        ) : 'Friend Requests';
      case 2:
        return 'Find Friends';
      case 3:
        return 'Activity Feed';
      default:
        return 'Tab';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom 
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <People />
              Friends & Social
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Connect with friends, share your progress, and stay motivated together
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={friendsLoading || socialLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Alerts */}
      {friendsError && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setFriendsError(null)}
        >
          {friendsError}
        </Alert>
      )}
      
      {socialError && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setSocialError(null)}
        >
          {socialError}
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Main Panel */}
        <Grid item xs={12} md={8}>
          {/* Tabs */}
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            sx={{ mb: 3 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              icon={<People />} 
              iconPosition="start"
              label={getTabLabel(0, friends.length)} 
            />
            <Tab 
              icon={<PersonAdd />} 
              iconPosition="start"
              label={getTabLabel(1, friendRequests.length)} 
            />
            <Tab 
              icon={<Search />} 
              iconPosition="start"
              label={getTabLabel(2)} 
            />
            <Tab 
              icon={<Timeline />} 
              iconPosition="start"
              label={getTabLabel(3)} 
            />
          </Tabs>

          {/* Tab Content */}
          <Card>
            <CardContent>
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    My Friends ({friends.length})
                  </Typography>
                  {friendsLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <FriendsList
                      friends={friends}
                      onRemove={handleRemoveFriend}
                      onMessage={handleSendMessage}
                      onViewProfile={handleViewProfile}
                      loading={friendsLoading}
                    />
                  )}
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Friend Requests ({friendRequests.length})
                  </Typography>
                  <FriendRequests
                    requests={friendRequests}
                    onAccept={handleAcceptFriendRequest}
                    onDecline={handleDeclineFriendRequest}
                    loading={friendsLoading}
                  />
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Find Friends
                  </Typography>
                  <FriendSearch
                    onSearch={searchUsers}
                    onSendRequest={handleSendFriendRequest}
                    searchResults={searchResults}
                    loading={searchLoading}
                  />
                </Box>
              )}

              {activeTab === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Friends Activity Feed
                  </Typography>
                  {socialLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ActivityFeed
                      activities={activityFeed}
                      onLike={handleLike}
                      onComment={handleComment}
                      loading={socialLoading}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Friend Leaderboard */}
            <FriendLeaderboard
              leaderboard={leaderboard}
              period="weekly"
              onPeriodChange={fetchLeaderboard}
              loading={socialLoading}
            />

            {/* Quick Stats */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Stats
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Friends:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {friends.length}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Pending Requests:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {friendRequests.length}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Recent Activities:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {activityFeed.length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar for messages */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FriendsPageComponent;